/**
 * SharePillView — presentational removable selection pill for the share
 * flows (SE-5527, folding the two former local copies: the desktop
 * ShareWatchlistModal pill from SE-5013 and the MobileShareWatchlistSheet
 * pill from SE-5077).
 *
 * Variants keep each surface's shipped spec verbatim:
 * - `solid` — the desktop modal look: info-soft fill (the Badge atom's
 *   `TONE_CLASS["info-soft"]` pairing, so the raw shades stay atom-internal).
 * - `tinted` — the mobile sheet look per Figma frame 40000726:56514:
 *   status-info border + 15% tinted fill + status-info text.
 * @module components/molecules/SharePill/SharePillView
 */
import { XIcon } from "lucide-react-native";
import React, { memo } from "react";

import { TONE_CLASS } from "@/components/atoms/Badge";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Variant → box/text class pairing (closed). */
const VARIANT_CLASS: Record<
  "solid" | "tinted",
  { readonly box: string; readonly text: string }
> = {
  solid: {
    box: TONE_CLASS["info-soft"].box,
    text: TONE_CLASS["info-soft"].text,
  },
  tinted: {
    box: "border border-status-info bg-status-info/15",
    text: "font-medium text-status-info",
  },
};

/** Props for SharePillView. */
export interface SharePillViewProps {
  /** Display name shown on the pill. */
  readonly name: string;
  /** Remove handler, pre-bound to this pill's userId by the Container. */
  readonly onRemove: () => void;
  /** Visual variant — `solid` (desktop modal) or `tinted` (mobile sheet). */
  readonly variant: "solid" | "tinted";
  /** Test identity forwarded from the call site (e.g. `pillFor(userId)`). */
  readonly testID?: string;
}

/**
 * Single removable selection pill.
 * @param root0 - View props
 * @param root0.name - Display name
 * @param root0.onRemove - Bound remove handler
 * @param root0.variant - Visual variant
 * @param root0.testID - Test identity from the call site
 * @returns The pill element.
 */
const SharePillView = ({
  name,
  onRemove,
  variant,
  testID,
}: SharePillViewProps) => (
  <Pressable
    testID={testID}
    onPress={onRemove}
    accessibilityRole="button"
    accessibilityLabel={`Remove ${name} from selection`}
    className={`flex-row items-center gap-1 rounded-full py-1 pl-3 pr-2 ${VARIANT_CLASS[variant].box}`}
  >
    <Text
      variant="caption"
      className={VARIANT_CLASS[variant].text}
      numberOfLines={1}
    >
      {name}
    </Text>
    <Icon as={XIcon} size="2xs" className={VARIANT_CLASS[variant].text} />
  </Pressable>
);

SharePillView.displayName = "SharePillView";

export default memo(SharePillView);
